import { agentDisplayMap } from '@/components/Agents/AgentUtils';
import { _isNil, _notNil } from '@/littledash';
import type { Edge, FitViewOptions, Node } from '@xyflow/react';
import { RiCheckboxCircleLine, RiCloseCircleLine, RiLoader4Line } from 'react-icons/ri';
import type {
  GraphCanvasConfig,
  GraphCanvasNode,
  GraphCanvasSize,
  GraphCanvasSnapshot,
  SiblingComparator,
} from '@/components/UI/GraphCanvas/GraphCanvas.model';
import { treeLayout } from '@/components/UI/GraphCanvas/GraphCanvas.utils';
import panelStyles from './AgentSessionPanel.module.scss';
import type {
  ActionCardNodeData,
  ButtonNodeData,
  GraphNodeBadge,
  ReadOnlyNodeData,
} from '@/components/UI/GraphCanvas/nodes/Nodes.utils';
import type {
  AgentSessionApiId,
  AgentInputV1,
  AgentSessionStatus,
  AgentStreamEventV1,
  AgentTreeNodeApiId,
  AgentTreeNodeLinkV1,
  AgentTreeNodeType,
  AgentTreeNodeV1,
  AgentTreeSessionV1,
  AgentTreeV1,
  AgentType,
} from '@/model/Agent.model';
import { web as webRoute } from '@/support/route';
import { StringUtils } from '@/utils/String.utils';

/** The `tree_node_created` variant of the SSE stream union — carries everything to build a node incrementally. */
type TreeNodeCreatedEvent = Extract<AgentStreamEventV1, { type: 'tree_node_created' }>;

export type { AgentSessionStatus, AgentTreeNodeType };

/**
 * Derivation metadata carried on every tree node's `data`, independent of how the node renders. Edge
 * derivation reads `parentApiId`; sibling ordering reads `sequenceNumber`; `type` is the node's
 * structural position in the tree (root/result). Kept alongside the render payload because the
 * GraphCanvas engine derives edges/layout purely from node `data`. `variant` is NOT here — it is the
 * render discriminant and lives on each union arm below.
 */
interface AgentTreeNodeBase extends Record<string, unknown> {
  apiId: AgentTreeNodeApiId;
  parentApiId: AgentTreeNodeApiId | null;
  type: AgentTreeNodeType;
  sequenceNumber: number;
}

/**
 * The `TData` carried on each React Flow node: derivation metadata plus the generic render payload for
 * whichever GraphCanvas node component renders it. Discriminated on `variant` — the same field that
 * drives node-component routing (`node.type = variant`) — so the payload and the rendered component
 * cannot drift: `read_only` carries a `ReadOnlyNode` payload (session title + status badge), `report`
 * `send_result` and `study_draft` a `ButtonNode` payload (`study_draft` via an `href`), and
 * `send_domain_confirm` an `ActionCardNode` payload. Agent type/status are folded in at map time.
 */
export type AgentTreeNodeData =
  | (AgentTreeNodeBase & { variant: 'read_only' } & ReadOnlyNodeData)
  | (AgentTreeNodeBase & { variant: 'report' } & ButtonNodeData)
  | (AgentTreeNodeBase & { variant: 'send_result' } & ButtonNodeData)
  | (AgentTreeNodeBase & { variant: 'send_domain_confirm' } & ActionCardNodeData)
  | (AgentTreeNodeBase & { variant: 'study_draft' } & ButtonNodeData);

/** Session-level metadata held as the engine's `TMeta`; drives the surrounding page chrome. */
export interface AgentSessionMeta extends Record<string, unknown> {
  apiId: AgentSessionApiId;
  agentType: AgentType;
  title: string | null;
  status: AgentSessionStatus;
  inputs: Array<AgentInputV1> | null;
}

/** A React Flow node specialised with the agent tree's render payload. */
export type AgentGraphNode = GraphCanvasNode<AgentTreeNodeData>;

/** Opens a result node's report, keyed by the node's api id. Injected so mapping stays free of React. */
export type ViewReportHandler = (nodeApiId: AgentTreeNodeApiId) => void;

/**
 * Opens a SEND validator result, keyed by the session (the typed result lives on the session's
 * validation execution, not the node). Injected so mapping stays free of React. Parallels
 * {@link ViewReportHandler}, but a `send_result` node carries no per-node payload to fetch — the modal
 * reads the typed bundle result off the session — so the handler needs no node id.
 */
export type SendViewResultsHandler = () => void;

/**
 * Opens the SEND domain-confirmation panel, keyed by the session (the per-file detections live on the
 * session's detect execution, not the node). Injected so mapping stays free of React. Like
 * {@link SendViewResultsHandler}, a `send_domain_confirm` node carries no per-node payload — the panel
 * reads the detected bundle off the session — so the handler needs no node id.
 */
export type SendConfirmDomainsHandler = () => void;

/** The click handlers a result node's action can be wired to, injected through the mapping functions. */
export interface AgentNodeHandlers {
  onViewReport?: ViewReportHandler;
  onSendViewResults?: SendViewResultsHandler;
  onSendConfirmDomains?: SendConfirmDomainsHandler;
}

/** Placeholder position; the layout pass computes the real coordinates. */
const PLACEHOLDER_POSITION = { x: 0, y: 0 } as const;

const STUDY_DRAFT_LABEL = 'View Study Draft';

/** The server sends ids only, so route knowledge lives here. */
const linkHref = (link: AgentTreeNodeLinkV1 | undefined): string | undefined => {
  if (_isNil(link)) {
    return undefined;
  }
  switch (link.type) {
    case 'study_draft':
      return webRoute('studies.new.draft', { formId: link.form_id, draftId: link.draft_id });
  }
};

/** Derived together so a node can never look live with nothing behind it. */
const studyDraftDestination = (link: AgentTreeNodeLinkV1 | undefined): { href?: string; disabled: boolean } => {
  const href = linkHref(link);
  return { href, disabled: _isNil(href) };
};

/**
 * Maps a session status to the corner badge shown on the root card: a tick when completed, a red X when
 * failed/cancelled, and a spinning loader while running. This is the one place agent status becomes
 * presentation — the generic `ReadOnlyNode` only knows about icon + tone + spin. Exported so other
 * surfaces showing a session's status (e.g. the study overview's health-check card) match the graph.
 */
export const statusBadge = (status: AgentSessionStatus): GraphNodeBadge | undefined => {
  switch (status) {
    case 'completed':
      return { icon: RiCheckboxCircleLine, tone: 'success', tooltip: 'Completed' };
    case 'failed':
      return { icon: RiCloseCircleLine, tone: 'error', tooltip: 'Failed' };
    case 'cancelled':
      return { icon: RiCloseCircleLine, tone: 'error', tooltip: 'Cancelled' };
    case 'running':
      return { icon: RiLoader4Line, tone: 'neutral', tooltip: 'Running', spin: true };
    default:
      return undefined;
  }
};

/**
 * Maps a tree-node DTO to a React Flow node. The DTO `variant` (`read_only`/`report`) is both the
 * top-level `node.type` (so React Flow routes to the matching generic component) AND the discriminant
 * the render payload is branched on — a single source of truth, so the payload and the rendered
 * component cannot drift. The structural `type` (`root`/`result`) is kept on `data` for reference.
 * All agent-specific presentation is resolved here: a `read_only` node becomes a `ReadOnlyNode` (a
 * per-agent status title + session-status badge; the agent name lives in the breadcrumb, not the node),
 * a `report` node a `ButtonNode` ("View Report"). The node components hold no agent knowledge.
 */
const mapNode = (dto: AgentTreeNodeV1, session: AgentTreeSessionV1, handlers?: AgentNodeHandlers): AgentGraphNode => {
  const base = {
    apiId: dto.api_id,
    parentApiId: dto.parent_api_id ?? null,
    type: dto.type,
    sequenceNumber: dto.sequence_number,
  };

  // Exhaustive over the node variant: a new variant added to the DTO union (e.g. question, link)
  // becomes a compile error here until its render payload is defined, rather than silently falling
  // through to a default.
  const data: AgentTreeNodeData = ((): AgentTreeNodeData => {
    switch (dto.variant) {
      case 'read_only':
        return {
          ...base,
          variant: 'read_only',
          title: agentDisplayMap[session.agent_type].title,
          badge: statusBadge(session.status),
        };
      case 'report':
        return {
          ...base,
          variant: 'report',
          label: 'View Report',
          // Disabled until a report opener is wired in; the button becomes actionable once supplied.
          disabled: _isNil(handlers?.onViewReport),
          onClick: _notNil(handlers?.onViewReport) ? () => handlers.onViewReport?.(dto.api_id) : undefined,
        };
      case 'send_result':
        return {
          ...base,
          variant: 'send_result',
          label: 'View results',
          // The typed SEND result lives on the session, not the node — the opener needs no node id.
          disabled: _isNil(handlers?.onSendViewResults),
          onClick: _notNil(handlers?.onSendViewResults) ? () => handlers.onSendViewResults?.() : undefined,
        };
      case 'send_domain_confirm':
        return {
          ...base,
          variant: 'send_domain_confirm',
          title: 'Confirm SEND domains',
          subtitle: "Review each file's detected domain",
          // The per-file detections live on the session's detect execution — the opener needs no node id.
          disabled: _isNil(handlers?.onSendConfirmDomains),
          onClick: _notNil(handlers?.onSendConfirmDomains) ? () => handlers.onSendConfirmDomains?.() : undefined,
        };
      case 'study_draft':
        return {
          ...base,
          variant: 'study_draft',
          label: STUDY_DRAFT_LABEL,
          ...studyDraftDestination(dto.link),
        };
    }
  })();

  return { id: dto.api_id, type: dto.variant, position: { ...PLACEHOLDER_POSITION }, data };
};

/**
 * Builds a graph node from a live `tree_node_created` stream event. Structural fields come from the
 * event; session-derived presentation (title, status badge) comes from `meta` (the hydrated session
 * held in the engine). Produces the same `AgentGraphNode` shape as {@link mapNode} so streamed and
 * snapshot nodes are indistinguishable to the canvas (and `upsertNodes` dedups any overlap by id).
 */
export const mapTreeNodeCreated = (
  event: TreeNodeCreatedEvent,
  meta: AgentSessionMeta,
  handlers?: AgentNodeHandlers
): AgentGraphNode => {
  const base = {
    apiId: event.node_api_id,
    parentApiId: event.parent_node_api_id ?? null,
    type: event.node_type,
    sequenceNumber: event.sequence_number,
  };

  const data: AgentTreeNodeData = ((): AgentTreeNodeData => {
    switch (event.node_variant) {
      case 'read_only':
        return {
          ...base,
          variant: 'read_only',
          title: agentDisplayMap[meta.agentType].title,
          badge: statusBadge(meta.status),
        };
      case 'report':
        return {
          ...base,
          variant: 'report',
          label: 'View Report',
          disabled: _isNil(handlers?.onViewReport),
          onClick: _notNil(handlers?.onViewReport) ? () => handlers.onViewReport?.(event.node_api_id) : undefined,
        };
      case 'send_result':
        return {
          ...base,
          variant: 'send_result',
          label: 'View results',
          disabled: _isNil(handlers?.onSendViewResults),
          onClick: _notNil(handlers?.onSendViewResults) ? () => handlers.onSendViewResults?.() : undefined,
        };
      case 'send_domain_confirm':
        return {
          ...base,
          variant: 'send_domain_confirm',
          title: 'Confirm SEND domains',
          subtitle: "Review each file's detected domain",
          disabled: _isNil(handlers?.onSendConfirmDomains),
          onClick: _notNil(handlers?.onSendConfirmDomains) ? () => handlers.onSendConfirmDomains?.() : undefined,
        };
      case 'study_draft':
        // No destination on the delta; the reconcile supplies the href.
        return { ...base, variant: 'study_draft', label: STUDY_DRAFT_LABEL, disabled: true };
    }
  })();

  return { id: event.node_api_id, type: event.node_variant, position: { ...PLACEHOLDER_POSITION }, data };
};

/** Derives edges from node parentage; edge id is `{parent}-{child}`, root (no parent) yields none. */
export const deriveAgentEdges = (nodes: ReadonlyArray<AgentGraphNode>): Array<Edge> =>
  nodes.reduce<Array<Edge>>((edges, node) => {
    const parentApiId = node.data.parentApiId;
    if (_notNil(parentApiId)) {
      edges.push({ id: `${parentApiId}-${node.id}`, source: parentApiId, target: node.id });
    }
    return edges;
  }, []);

/**
 * Orders sibling nodes by their server-assigned `sequenceNumber` (creation order), falling back to
 * api-id for a stable tiebreak. `atn_` ids are random FriendlyIds, so sorting by id alone would give
 * an arbitrary left-to-right order once a level has more than one node.
 */
const compareBySequence: SiblingComparator<AgentTreeNodeData> = (a, b) => {
  const bySequence = a.data.sequenceNumber - b.data.sequenceNumber;
  return bySequence !== 0 ? bySequence : StringUtils.compareLexicographic(a.id, b.id);
};

/** GraphCanvasEngine config for the agent session tree: parentage-based edges + sequence-ordered tree layout. */
export const agentTreeGraphConfig: GraphCanvasConfig<AgentTreeNodeData> = {
  deriveEdges: deriveAgentEdges,
  layout: treeLayout(compareBySequence),
};

/**
 * Distance from the canvas top at which the tree is pinned, matching the `margin: $spacing-large` (2rem)
 * the view applies to `.react-flow__panel` (see AgentSessionView.module.scss — change both together).
 * Aligning the tree's top edge to the panels' top edge is what stops a two-node tree floating in the
 * middle of a viewport-tall canvas. A plain literal rather than an SCSS `:export`, because the fit needs
 * a number here and the export is a px string.
 */
export const AGENT_TREE_TOP_INSET_PX = 32;

/**
 * Canvas width at/below which the zoom cap stays at its baseline. Sized to the minimum supported viewport
 * (1272px, see the responsive-design rule in CLAUDE.md) — the canvas, not the window, is what the fit
 * measures, so the app's sidebar means the ramp actually starts a little above that viewport width.
 */
const BASELINE_CANVAS_WIDTH = 1272;
/** Width at/above which the zoom cap reaches its ceiling; beyond this, more width buys no more zoom. */
const WIDE_CANVAS_WIDTH = 2400;
const BASELINE_MAX_ZOOM = 1.25;
/**
 * Ceiling anchored to the type scale rather than to taste: zoom scales a node's text along with
 * everything else, and 1.4 keeps its `$font-size-6` (14px) title at ~19.6px — just under the 20px
 * `$font-size-4` panel headers, the largest type on the page. Going higher makes the tree's text the
 * biggest on screen, at which point it reads as a zoomed diagram rather than part of the app's UI.
 */
const WIDE_MAX_ZOOM = 1.4;

/**
 * The zoom cap for the tree, scaled to the canvas width. A tree of two or three nodes is far smaller
 * than the canvas, so its fit is decided by this cap rather than by the available space — which is why
 * a fixed cap left a small tree marooned in whitespace on a large screen. Ramps linearly from
 * {@link BASELINE_MAX_ZOOM} at {@link BASELINE_CANVAS_WIDTH} to {@link WIDE_MAX_ZOOM} at
 * {@link WIDE_CANVAS_WIDTH}, so the nodes grow with the screen while narrow canvases keep today's
 * behaviour untouched.
 *
 * Scaling zoom (rather than the nodes' own CSS width) keeps every part of a node — text, borders,
 * padding, the gaps between siblings — in proportion, and so cannot make siblings collide the way a
 * fluid node width would once it outgrew the tree layout's fixed sibling pitch (`SIBLING_WIDTH` in
 * GraphCanvas.utils.ts, 360px against a 16rem node).
 */
export const agentTreeMaxZoom = (canvasWidth: number): number => {
  const span = WIDE_CANVAS_WIDTH - BASELINE_CANVAS_WIDTH;
  const progress = Math.min(Math.max((canvasWidth - BASELINE_CANVAS_WIDTH) / span, 0), 1);
  return BASELINE_MAX_ZOOM + progress * (WIDE_MAX_ZOOM - BASELINE_MAX_ZOOM);
};

/**
 * Narrows a CSS-module `:export` value to React Flow's px-literal padding type, falling back when it isn't a
 * px length. A bare `as` cast would be a lie in two environments: under the unit tests' `css: false` a CSS
 * module resolves to a Proxy returning a scoped class name for *any* key, and if the `:export` is ever
 * renamed or dropped the value is `undefined` at runtime. React Flow's `parsePadding` answers an unparseable
 * value with a `console.error` and 0 padding — which would drop the panel channel and lay nodes under the
 * panels — so validate here rather than discovering it there.
 *
 * Takes `unknown` deliberately: Vite types a CSS module as `{ readonly [key: string]: string }`, so a missing
 * key is typed `string` while actually being `undefined` — the exact claim this function exists to stop
 * trusting.
 *
 * The fallback must stay a px string. `PaddingWithUnit`'s bare-`number` member is a *ratio*, so `parseFloat`
 * would hand React Flow a padding of 448x the content — shrinking the graph to ~1/449 of the canvas — rather
 * than reserving 448px.
 *
 * Exported for testing: the fallback branch is the only one unit tests can reach through the real stylesheet,
 * so the accept branch has to be exercised directly.
 */
const PX_LENGTH_PATTERN = /^\d+(?:\.\d+)?px$/;

export const asPxLength = (value: unknown, fallback: `${number}px`): `${number}px` =>
  typeof value === 'string' && PX_LENGTH_PATTERN.test(value) ? (value as `${number}px`) : fallback;

// Module constants: neither varies with the canvas. The inset needs the annotation because an inline
// interpolation widens to `string` and fails React Flow's `PaddingWithUnit`.
const PANEL_RESERVE_PADDING = asPxLength(panelStyles.panelReservePx, '448px');
const TOP_INSET_PADDING: `${number}px` = `${AGENT_TREE_TOP_INSET_PX}px`;

/**
 * fitView options for the agent session canvas, resolved against the measured canvas. The
 * Configuration/Activity panels are absolutely-positioned React Flow overlays that reserve no layout
 * space, so without padding the fit would lay nodes out across the full canvas — including beneath the
 * panels. Reserves each panel's horizontal footprint (`panelReservePx`, exported from
 * AgentSessionPanel.module.scss so SCSS stays the source of truth) as left/right padding; top/bottom
 * reserve the same gap the panels sit at. `maxZoom` scales with the canvas — see {@link agentTreeMaxZoom}.
 */
export const buildAgentTreeFitViewOptions = ({ width }: GraphCanvasSize): FitViewOptions<Node<AgentTreeNodeData>> => ({
  padding: {
    left: PANEL_RESERVE_PADDING,
    right: PANEL_RESERVE_PADDING,
    top: TOP_INSET_PADDING,
    bottom: TOP_INSET_PADDING,
  },
  maxZoom: agentTreeMaxZoom(width),
});

/**
 * Maps a full `GET /tree` response to the GraphCanvas snapshot shape (meta + nodes). The `handlers`, if
 * supplied, are wired onto each result node's action: `onViewReport` opens a `report` node's report;
 * `onSendViewResults` opens a `send_result` node's typed SEND results.
 */
export const mapTreeToSnapshot = (
  tree: AgentTreeV1,
  handlers?: AgentNodeHandlers
): GraphCanvasSnapshot<AgentTreeNodeData, AgentSessionMeta> => ({
  meta: {
    apiId: tree.session.api_id,
    agentType: tree.session.agent_type,
    title: tree.session.title ?? null,
    status: tree.session.status,
    inputs: tree.session.inputs ?? null,
  },
  nodes: tree.nodes.map((node) => mapNode(node, tree.session, handlers)),
});
